import {
  ADD_PAGE_MENTION,
  AUTH_ADD_OR_UPDATED,
  AUTH_LIST_UPDATE,
  DELETE_LISTING_GROUP,
  DISMISS_ALERTS,
  REMOVE_PAGE_MENTION,
  SET_AUTH_LIST,
  SET_SETTINGS_NOT_LOADED,
  UPDATE_FILTERS,
  UPDATE_PROVIDER_FILTERS
} from './constants';

import _ from 'lodash';

/**
 * Reducer responsible for keeping chatter settings state, and its filters
 *
 * For the most part, the entire state is loaded up all at once, for all providers
 * So far, the initial loading time has seemed okay, but this may need optimizations
 * for larger accounts, (above 1500 listings)
 *
 * Following entity updates are sent incrementally by the server in light-weight ADD or REMOVE events
 */

const defaultFilter = {
  pageSize: 10,
  listingPageIndex: 0,
  listingSortOrder: 1,
  listingGroupIndex: 0,
  listingGroupSortOrder: 1,
  listingGroupPageIndex: 0,
  listingGroupPageSortOrder: 1,
  textSearch: null,
  onlyIgGraphId: false,
  onlySourceType: null,
  onlySourceAccount: null,
  subSectionIndex: 0
};
const igFilter = {
  pageSize: 10,
  listingPageIndex: 0,
  listingSortOrder: 1,
  listingGroupIndex: 0,
  listingGroupSortOrder: 1,
  listingGroupPageIndex: 0,
  listingGroupPageSortOrder: 1,
  textSearch: null,
  onlyIgGraphId: true,
  onlySourceType: null,
  onlySourceAccount: null,
  subSectionIndex: 0
};

const initialState = {
  isLoadingUserAccounts: true,
  userAccountsList: [],
  accountAlerts: {},
  settingsPayload: {
    userAccounts: [],
    listings: [],
    updateInfo: {},
    customListings: [],
    customSearches: [],
    chatmeterListings: [],
    chatmeterLocations: [],
  },
  settingsPayloadIndexes: {
    userAccounts: {},
    listings: {},
    customListings: {},
    customSearches: {},
    chatmeterListings: {},
    chatmeterLocations: {},
  },
  filters: {
    selectedTabIndex: 0,
    providers: {
      'Facebook': defaultFilter,
      'Twitter': defaultFilter,
      'Instagram': igFilter,
      'Google': defaultFilter
    }
  }
};


/**
 * Various re-usable state mapping / update functions
 */


/**
 *
 * Applies search filters and pagination to listings
 * Returns Array of Array of ChatterListings
 *
 * @param {*} config
 * @param {*} chatterListings
 * @param {*} fromGroup
 */
const applyFiltersSortsAndPaginationToListings = (config, chatterListings, fromGroup) => {
  const hasSourceType = {
    'customListing': l => !!l.customChatterListingId,
    'userAccount': l => !!l.userAccountId,
    'storePage': l => !!l.locationId
  };

  const filtered = chatterListings.filter(l => {
    const matchSourceType = !config.onlySourceType || hasSourceType[config.onlySourceType](l);
    const matchSourceAccount = !config.onlySourceAccount
      || l.userAccountId.filter(x =>
        ( x === config.onlySourceAccount ) || ( config.onlySourceAccount.indexOf(x) >= 0 )
      ).length;
    const textSearch = config.textSearch && normalizeTextForSearchMatching(config.textSearch);
    // TODO - match against more fields (at least location address fields. possibly id fields. location name)
    const matchText = !textSearch
      || (l.searchMatcherTerms && l.searchMatcherTerms.find(x => x.match(textSearch)));
    const matchIgGraphId = ! config.onlyIgGraphId || (l.chatmeterListing && l.chatmeterListing.providerIds.igGraphId);
    const filterGoogleToCredentialPresent = !(l.provider === 'Google') || (!!l.credentialIds && l.credentialIds.length);
    const filterInstagramToAccountsPresent = !(l.provider === 'Instagram')
      || (!!l.userAccounts && l.userAccounts.length);
    return (
      matchSourceType
      && matchSourceAccount
      && matchText
      && matchIgGraphId
      && filterGoogleToCredentialPresent
      && filterInstagramToAccountsPresent
    );
  });

  const sorted = filtered.sort((a, b) =>
    a.name.localeCompare(b.name) * !fromGroup
      ? config.listingSortOrder
      : config.listingGroupPageSortOrder
  );

  let currentPage = [];
  const pages = [currentPage];

  sorted.forEach(l => {
    if (currentPage.length === config.pageSize) {
      currentPage = [];
      pages.push(currentPage);
    }
    currentPage.push(l);
  });


  return pages
};

/**
 *
 * Applies filters, sorts, and pagination to listing groups
 * Returns Array of Array of Listing Groups
 *
 * @param {*} config
 * @param {*} listingGroups
 */

const applyFiltersSortsAndPaginationToListingGroups = (config, listingGroups) => {

  let currentPage = [];
  const pages = [currentPage];

  const filtered = listingGroups.filter(l => {
    return !config.textSearch || l.name.toLowerCase().indexOf(config.textSearch.toLowerCase()) >= 0
  });

  const sorted = filtered.sort((a, b) => a.name.localeCompare(b.name) * config.listingGroupSortOrder);

  sorted.forEach(l => {
    if (currentPage.length === config.pageSize) {
      currentPage = [];
      pages.push(currentPage);
    }
    currentPage.push(l)
  });

  return pages
};

/**
 * Map of field types of settingsPayload state to entity id extractors
 */
const fieldIdGetters = {
  userAccounts: entity => entity._id.$oid,
  listings: entity => entity.remoteId,
  chatmeterLocations: entity => entity.locationId,
  chatmeterListings: entity => entity.listingId,
  customListings: entity => entity._id.$oid,
  customSearches: entity => entity._id.$oid
};

/**
 * Accepts an array of events of shape:
 * e.g.
 *
 * {
 *    "field:": "listings",        // can be any field name of `settingsPayload`
 *    "type": "ADD",               // "ADD" | "REMOVE"
 *    "entity": { ... }            // the entity, a ChatterListing in this case
 * }
 * and add or removes the entities from the settingsPayload
 * @param {*} updates
 */
const applyUpdateEvents = updates => state => {

  const getIdFromEntity = (collectionName, entity) => {
    return fieldIdGetters[collectionName](entity)
  };

  return {
    ...state,
    settingsPayload:  updates.reduce(
      (soFar, {type, field, entity}) => ({
        ...soFar,
        [field]: soFar[field]
          .filter(existing => getIdFromEntity(field, existing) !== getIdFromEntity(field, entity))
          .concat(type === 'ADD' ? [entity] : [])
      }),
      state.settingsPayload
    )
  }

};

const normalizeTextForSearchMatching = text =>
  text.split(/\s+/).map(sub => sub.replace(/[^\w|\s]+/g, '')).join(' ').toLowerCase();

/**
 * Creates a map for each of the specified keys  (userAccounts, listings, etc.) keyed by the entities primary key
 * e.g. {
 *    userAccounts: {
 *      '12345': { ... }
 *    },
 *    listings: { ... }
 *    ...
 * }
 */
const payloadListsToIndexes = (payload) => {
  return [
    'userAccounts',
    'listings',
    'chatmeterLocations',
    'chatmeterListings',
    'customListings',
    'customSearches',
  ].reduce((index, key) => {
    const list = payload[key];
    const thisIndex = {};
    const getId = fieldIdGetters[key];
    list.forEach(x => thisIndex[getId(x)] = x);
    return {
      ...index,
      [key]: thisIndex
    }
  }, {});
};

/**
 * Maps the settingsPayload, which is in a flat shape of fields to arrays,
 * to a tree shape more easily digestible by the view.
 * Also applies current filters to affected fields
 * @param {*} providerConfigs - for computing filtered fields
 * @param {*} settingsPayload
 *
 * Returns the more digestible state tree
 */
const payloadToStructuredSettings = (providerConfigs, {
  userAccounts,
  listings,
  updateInfo,
  customListings,
  customSearches,
  chatmeterListings,
  chatmeterLocations,
  listingGroups
}) => {
  const empty = {Twitter: [], Facebook: [], Instagram: [], Google: []};
  const withProviderDefault = map => ({
    ...empty, ...map
  });
  const sortedUserAccounts = userAccounts.sort((a, b) => a.remoteAccountName.localeCompare(b.remoteAccountName));
  const userAccountsByProvider = withProviderDefault(_.groupBy(sortedUserAccounts, a => a.provider));
  const userAccountsById = _.keyBy(userAccounts, a => a._id.$oid);
  const customListingsByProvider = withProviderDefault(_.groupBy(customListings, c => c.provider));
  const customListingsById = _.keyBy(customListings, l => l._id.$oid);
  const chatmeterListingsById = _.keyBy(chatmeterListings, l => l.listingId);
  const chatmeterLocationsById = _.keyBy(chatmeterLocations, l => l.locationId);
  const listingGroupsByProvider = withProviderDefault(_.groupBy(listingGroups, g => g.provider));

  const chatterListingsWithSources = listings.map( l => {
    const userAccounts = l.userAccountId.map(x => userAccountsById[x]).filter(x => x);
    const chatmeterListing = l.listingId && chatmeterListingsById[l.listingId];
    const chatmeterLocation = l.locationId && chatmeterLocationsById[l.locationId];
    const customListing = l.customChatterListingId && customListingsById[l.customChatterListingId];
    const joinDefinedStrings = joinString => (...args) => args.filter(x => x).join(joinString) || undefined;
    const formatAddress = addr => {

      return joinDefinedStrings(', ')(
        addr.street,
        addr.city,
        joinDefinedStrings(' ')(addr.state, addr.zip || addr.postalCode)
      )
    };
    const chatmeterAddress = chatmeterLocation
      && chatmeterLocation.businessAddress
      && formatAddress(chatmeterLocation.businessAddress);
    const externalAddress = l.address && formatAddress(l.address);
    const searchMatcherTerms = [ // add more fields here for more flexible search
      l.name,
      l.url,
      chatmeterAddress,
      externalAddress,
      l.locationId,
      l.remoteId,
    ].concat(
      userAccounts.reduce((foo, x) => foo.concat([x.remoteAccountname, x.userDetails.fullName]), [])
    )
      .filter(x => x)
      .map(normalizeTextForSearchMatching);
    return {
      ...l,
      userAccounts,
      chatmeterListing,
      chatmeterLocation,
      customListing,
      chatmeterAddress,
      externalAddress,
      searchMatcherTerms
    }
  });

  const listingsByProvider = withProviderDefault(_.groupBy(chatterListingsWithSources, l => l.provider));

  const getUpdateInfo = (fieldName, entity) => {
    let entityId = fieldIdGetters[fieldName](entity);
    return updateInfo[entityId] || {};
  };

  const toAccountAndUpdateInfo = account => ({
    updateInfo: getUpdateInfo('userAccounts', account),
    account
  });

  const addUpdateInfo = fieldName => entity => ({
    ...entity,
    updateInfo: getUpdateInfo(fieldName, entity)
  });

  const fbListings = listingsByProvider['Facebook'].map(addUpdateInfo('listings'));
  const igListings = listingsByProvider['Instagram'].map(addUpdateInfo('listings')).filter( (l) =>
    // These are pre filtered to be able to use the length as the count shown in the settings tab
    !!l.userAccounts && l.userAccounts.length && l.chatmeterListing && l.chatmeterListing.providerIds.igGraphId
  );

  const gListings = listingsByProvider['Google'].map(addUpdateInfo('listings'));
  return {
    missingProviderCredentials: (['Facebook', 'Twitter', 'Google']).filter(p => userAccountsByProvider[p].length === 0),
    Twitter: {
      accounts: userAccountsByProvider['Twitter'].map(toAccountAndUpdateInfo),
      customSearches
    },
    Facebook: {
      customListings: customListingsByProvider['Facebook'],
      listings: fbListings,
      listingPages: applyFiltersSortsAndPaginationToListings(providerConfigs['Facebook'], fbListings),
      listingGroups: listingGroupsByProvider['Facebook'],
      listingGroupPages: applyFiltersSortsAndPaginationToListingGroups(
        providerConfigs['Facebook'],
        listingGroupsByProvider['Facebook']
      ),
      accounts: userAccountsByProvider['Facebook'].map(acc => ({
        ...toAccountAndUpdateInfo(acc),
        listingsCount: fbListings.filter(l =>
          l.userAccountId.filter(x => x === acc._id.$oid).length && !l.isDisabled
        ).length
      }))
    },
    Instagram: {
      customListings: customListingsByProvider['Instagram'],
      listings: igListings,
      listingPages: applyFiltersSortsAndPaginationToListings(providerConfigs['Instagram'], igListings),
      listingGroupPages: applyFiltersSortsAndPaginationToListingGroups(
        providerConfigs['Instagram'],
        listingGroupsByProvider['Instagram']
      ),
      accounts: userAccountsByProvider['Instagram'].map(toAccountAndUpdateInfo)
    },
    Google: {
      listings: gListings,
      customListings: customListingsByProvider['Google'],
      listingPages: applyFiltersSortsAndPaginationToListings(providerConfigs['Google'], gListings),
      listingGroups: listingGroupsByProvider['Google'],
      listingGroupPages: applyFiltersSortsAndPaginationToListingGroups(
        providerConfigs['Google'],
        listingGroupsByProvider['Google']
      ),
      accounts: userAccountsByProvider['Google'].map(toAccountAndUpdateInfo)
    }
  }

};

const recomputeStructuredSettings = state => {
  return {
    ...state,
    settingsPayloadIndexes: payloadListsToIndexes(state.settingsPayload),
    userAccountsList: payloadToStructuredSettings(state.filters.providers, state.settingsPayload)
  };
};

const reducers = {
  [SET_AUTH_LIST]: (state, { auth }) => {
    const updatedUserAccounts = auth.userAccounts.map(account => {
      if (account.provider === "GoogleMap") return { ...account, provider: "Google" };
      return account;
    });
    const settingsPayload = {...auth, userAccounts: updatedUserAccounts};
    return recomputeStructuredSettings({
      ...state,
      isLoadingUserAccounts: false,
      settingsPayload: settingsPayload
    });
  },
  [AUTH_LIST_UPDATE]: (state, { payload }) => recomputeStructuredSettings(applyUpdateEvents(payload)({
    ...state,
    isLoadingUserAccounts: false
  })),
  [SET_SETTINGS_NOT_LOADED]: (state, _) => {
    return ({
      ...state,
      isLoadingUserAccounts: true
    })
  },
  [AUTH_ADD_OR_UPDATED]: (state, {auth}) => ({
    ...state,
    accountAlerts: {
      ...state.accountAlerts,
      [auth.provider]: auth.wasUpdated ? `"${auth.remoteAccountName}" successfully updated.`
      : `Your account "${auth.remoteAccountName}" has successfully been added.`
    }
  }),
  [DISMISS_ALERTS]: (state, _) => ({
    ...state,
    accountAlerts: {}
  }),
  [UPDATE_PROVIDER_FILTERS]: (state, event) => recomputeStructuredSettings({
    ...state,
    filters: {
      ...state.filters,
      providers: {
        ...state.filters.providers,
        [event.provider]: {
          ...state.filters.providers[event.provider],
          ...event.update
        }
      }
    }
  }),

  [UPDATE_FILTERS]: (state, event) => ({
    ...state,
    filters: {
      ...state.filters,
      ...event.update
    }
  }),

  [DELETE_LISTING_GROUP]: (state, {response}) => {
    let listingGroups = _.filter(state.settingsPayload.listingGroups, lg => (lg._id.$oid !== response.deleted));
    return recomputeStructuredSettings({
    ...state,
      settingsPayload: {...state.settingsPayload,listingGroups},
      isLoadingUserAccounts: false
    });
  },

  [ADD_PAGE_MENTION]: (state, {_, provider, mention}) => {
    if(state.userAccountsList[provider]){
      return {
        ...state,
        userAccountsList: {
          ...state.userAccountsList,
          [provider]: {
            ...state.userAccountsList[provider],
            customSearches: [...state.userAccountsList[provider].customSearches, {search: mention}]
          }
        }
      };
    }
    return state;
  },
  [REMOVE_PAGE_MENTION]: (state, {_, provider, mention}) => {
    if(state.userAccountsList[provider]){
      return {
        ...state,
        userAccountsList: {
          ...state.userAccountsList,
          [provider]: {
            ...state.userAccountsList[provider],
            customSearches: [...state.userAccountsList[provider].customSearches.filter(s => s.search !== mention)]
          }
        }
      };
    }
    return state;
  }
};

export default function settingsReducer(state = initialState, action) {
  const fn = reducers[action.type];
  return fn ? fn(state, action) : state
}
